/**
 * Session cache for Dashboard widgets.
 *
 * Prefetch starts as soon as a JWT is written (login / SSO), so the first
 * Dashboard paint can hydrate from memory instead of waiting on cold GETs.
 * Cases / flags / watchlist stay in-memory only. Entity stats and supported
 * chains may snapshot to sessionStorage (catalog data, not officer-private).
 */
import { useEffect, useState } from 'react';
import {
    bulkImportService,
    caseService,
    entityService,
    flagService,
    searchService,
    systemService,
    watchlistService,
} from '../services/api';

export const DASHBOARD_CACHE_TTL_MS = 60_000;
/** Public catalog (entity stats, chain list) — safe to reuse across re-login. */
export const PUBLIC_CATALOG_TTL_MS = 24 * 60 * 60 * 1000;

export type DashboardCacheKey =
    | 'entityStats'
    | 'supportedChains'
    | 'cases'
    | 'flags'
    | 'watchlist'
    | 'recentlyViewed'
    | 'latestImports';

const PUBLIC_KEYS: ReadonlySet<DashboardCacheKey> = new Set(['entityStats', 'supportedChains']);
const PRIVATE_KEYS: readonly DashboardCacheKey[] = [
    'cases',
    'flags',
    'watchlist',
    'recentlyViewed',
    'latestImports',
];
const SESSION_PREFIX = 'dashboard-cache:';

type CacheEntry = { value: unknown; fetchedAt: number };

const memory = new Map<DashboardCacheKey, CacheEntry>();
const errors = new Map<DashboardCacheKey, string | undefined>();
const loading = new Map<DashboardCacheKey, boolean>();
const listeners = new Set<() => void>();

let inflight: Promise<void> | null = null;

function notify(): void {
    for (const cb of listeners) cb();
}

function sessionKey(key: DashboardCacheKey): string {
    return `${SESSION_PREFIX}${key}`;
}

function readPublicSession(key: DashboardCacheKey): CacheEntry | undefined {
    if (!PUBLIC_KEYS.has(key)) return undefined;
    try {
        const raw = sessionStorage.getItem(sessionKey(key));
        if (!raw) return undefined;
        const parsed = JSON.parse(raw) as CacheEntry;
        if (!parsed || typeof parsed.fetchedAt !== 'number') return undefined;
        return parsed;
    } catch {
        return undefined;
    }
}

function writePublicSession(key: DashboardCacheKey, entry: CacheEntry): void {
    if (!PUBLIC_KEYS.has(key)) return;
    try {
        sessionStorage.setItem(sessionKey(key), JSON.stringify(entry));
    } catch {
        /* quota / private mode */
    }
}

function clearPublicSession(): void {
    for (const key of PUBLIC_KEYS) {
        try {
            sessionStorage.removeItem(sessionKey(key));
        } catch {
            /* ignore */
        }
    }
}

function store(key: DashboardCacheKey, value: unknown): void {
    const entry: CacheEntry = { value, fetchedAt: Date.now() };
    memory.set(key, entry);
    errors.set(key, undefined);
    loading.set(key, false);
    writePublicSession(key, entry);
    notify();
}

function markError(key: DashboardCacheKey, message: string): void {
    errors.set(key, message);
    loading.set(key, false);
    notify();
}

export function getDashboardCached<T>(key: DashboardCacheKey): T | undefined {
    const hit = memory.get(key) ?? readPublicSession(key);
    if (!hit) return undefined;
    if (!memory.has(key)) memory.set(key, hit);
    return hit.value as T;
}

function cacheTtlMs(key: DashboardCacheKey): number {
    return PUBLIC_KEYS.has(key) ? PUBLIC_CATALOG_TTL_MS : DASHBOARD_CACHE_TTL_MS;
}

export function isDashboardCacheFresh(key: DashboardCacheKey): boolean {
    const hit = memory.get(key) ?? readPublicSession(key);
    if (!hit) return false;
    return Date.now() - hit.fetchedAt < cacheTtlMs(key);
}

function hydratePublicCatalogFromSession(): void {
    for (const key of PUBLIC_KEYS) {
        if (memory.has(key)) continue;
        const hit = readPublicSession(key);
        if (hit) memory.set(key, hit);
    }
}

export function getDashboardCacheError(key: DashboardCacheKey): string | undefined {
    return errors.get(key);
}

export function isDashboardCacheLoading(key: DashboardCacheKey): boolean {
    if (getDashboardCached(key) !== undefined) return false;
    if (errors.get(key)) return false;
    return true;
}

export function subscribeDashboardCache(cb: () => void): () => void {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
}

export function setDashboardCached(key: DashboardCacheKey, value: unknown): void {
    store(key, value);
}

export function invalidateDashboardKey(key: DashboardCacheKey): void {
    memory.delete(key);
    errors.delete(key);
    if (PUBLIC_KEYS.has(key)) {
        try {
            sessionStorage.removeItem(sessionKey(key));
        } catch {
            /* ignore */
        }
    }
    notify();
}

/** Drop officer-private widgets only; keep catalog snapshots for faster re-login. */
export function clearPrivateDashboardCache(): void {
    for (const key of PRIVATE_KEYS) {
        memory.delete(key);
        errors.delete(key);
        loading.delete(key);
    }
    inflight = null;
    hydratePublicCatalogFromSession();
    notify();
}

export function clearDashboardCache(): void {
    memory.clear();
    errors.clear();
    loading.clear();
    inflight = null;
    clearPublicSession();
    notify();
}

/** Test-only: wipe module state without going through session logout. */
export function resetDashboardCacheForTests(): void {
    clearDashboardCache();
}

function hasToken(): boolean {
    try {
        return Boolean(localStorage.getItem('token'));
    } catch {
        return false;
    }
}

function normalizeWatchlist(data: unknown): unknown[] {
    if (Array.isArray(data)) return data;
    const wrapped = data as { watchlist?: unknown[] } | null;
    return Array.isArray(wrapped?.watchlist) ? wrapped.watchlist : [];
}

function allFresh(): boolean {
    const keys: DashboardCacheKey[] = [
        'entityStats',
        'supportedChains',
        'cases',
        'flags',
        'watchlist',
        'recentlyViewed',
        'latestImports',
    ];
    return keys.every((k) => isDashboardCacheFresh(k));
}

async function loadKey(key: DashboardCacheKey, fn: () => Promise<unknown>): Promise<void> {
    loading.set(key, true);
    notify();
    try {
        const value = await fn();
        store(key, value);
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        markError(key, msg);
    }
}

async function runPrefetch(): Promise<void> {
    await Promise.allSettled([
        loadKey('entityStats', () => entityService.getStats()),
        loadKey('supportedChains', () => systemService.getSupportedChains()),
        loadKey('cases', async () => (await caseService.getAll()) || []),
        loadKey('flags', async () => {
            const data = await flagService.list();
            return data?.flags || [];
        }),
        loadKey('watchlist', async () => normalizeWatchlist(await watchlistService.getAll())),
        loadKey('recentlyViewed', async () => (await searchService.getRecentlyViewed(10)) || []),
        loadKey('latestImports', () => bulkImportService.getLatest(5)),
    ]);
}

export async function prefetchDashboard(opts?: { force?: boolean }): Promise<void> {
    if (!hasToken()) return;
    if (inflight) return inflight;
    if (!opts?.force && allFresh()) return;
    const run = runPrefetch();
    inflight = run;
    try {
        await run;
    } finally {
        if (inflight === run) inflight = null;
        notify();
    }
}

export async function refreshDashboardKey(key: DashboardCacheKey): Promise<void> {
    if (!hasToken()) return;
    invalidateDashboardKey(key);
    const loaders: Record<DashboardCacheKey, () => Promise<unknown>> = {
        entityStats: () => entityService.getStats(),
        supportedChains: () => systemService.getSupportedChains(),
        cases: async () => (await caseService.getAll()) || [],
        flags: async () => {
            const data = await flagService.list();
            return data?.flags || [];
        },
        watchlist: async () => normalizeWatchlist(await watchlistService.getAll()),
        recentlyViewed: async () => (await searchService.getRecentlyViewed(10)) || [],
        latestImports: () => bulkImportService.getLatest(5),
    };
    await loadKey(key, loaders[key]);
}

/** Login-page warmup: parse shell routes + ping health (no JWT). */
export function warmLoginShell(): void {
    void import('../pages/Dashboard');
    void import('../pages/Workbench');
    const base = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(/\/$/, '');
    void fetch(`${base}/health`).catch(() => {});
}

hydratePublicCatalogFromSession();

export function useDashboardCache(): {
    version: number;
    get: typeof getDashboardCached;
    error: typeof getDashboardCacheError;
    loading: typeof isDashboardCacheLoading;
} {
    const [version, setVersion] = useState(0);
    useEffect(() => subscribeDashboardCache(() => setVersion((n) => n + 1)), []);
    return { version, get: getDashboardCached, error: getDashboardCacheError, loading: isDashboardCacheLoading };
}
